import { useState } from "react";
import { RadioGroup } from "@headlessui/react";

import { useBookingProvidersQuery } from "~/services/admin/actions";
import type { BookingProvider } from "~/services/admin/types";
import { VISIT_TYPES } from "~/services/scheduling/constants";
import { useStatesListQuery } from "~/services/states/actions";
import { INSURANCE_NOT_FOUND_OPTION } from "~/shared.types";
import { customIcons } from "~/ui";
import { ProviderOption } from "../components/provider-option";
import { StaffBookingFooter } from "../components/staff-booking-footer";
import { StepHeading } from "../components/step-heading";

type ProviderSelectionStepProps = {
  userId: string;
  stateId: number;
  insuranceId: number | null;
  providerId: number | null;
  onContinue: (providerId: number) => void;
  onBack: () => void;
};

const ProviderGroup = ({
  title,
  providers,
}: {
  title: string;
  providers: BookingProvider[];
}) => {
  if (!providers.length) return null;

  return (
    <section className="flex flex-col gap-3">
      <h3 className="font-ivy text-indigo text-xl font-normal">{title}</h3>
      <ul className="flex flex-col gap-2">
        {providers.map((provider) => (
          <li key={provider.id}>
            <ProviderOption provider={provider} />
          </li>
        ))}
      </ul>
    </section>
  );
};

export const ProviderSelectionStep = ({
  userId,
  stateId,
  insuranceId,
  providerId,
  onContinue,
  onBack,
}: ProviderSelectionStepProps) => {
  const [selectedProviderId, setSelectedProviderId] = useState(providerId);

  const {
    data: providers,
    isPending,
    isError,
    refetch,
  } = useBookingProvidersQuery({
    stateId,
    userId,
    insuranceId:
      insuranceId === null || insuranceId === INSURANCE_NOT_FOUND_OPTION.value
        ? undefined
        : insuranceId,
  });
  const { data: states } = useStatesListQuery();
  const stateName = states?.find((state) => state.id === stateId)?.name;

  const inNetworkProviders =
    providers?.filter((provider) => provider.inNetwork) ?? [];
  const outOfNetworkProviders =
    providers?.filter((provider) => !provider.inNetwork) ?? [];
  const selectedProvider = inNetworkProviders.find(
    (provider) => provider.id === selectedProviderId,
  );

  const renderContent = () => {
    if (isPending) {
      return (
        <div role="status" className="flex justify-center py-10">
          <customIcons.SpinnerIcon
            aria-hidden="true"
            className="text-violet size-10 animate-spin"
          />
          <span className="sr-only">Loading providers</span>
        </div>
      );
    }

    if (isError) {
      return (
        <p className="text-base text-neutral-700">
          We could not load the providers.{" "}
          <button
            type="button"
            className="text-violet cursor-pointer underline"
            onClick={() => void refetch()}
          >
            Try again
          </button>
        </p>
      );
    }

    if (!providers.length) {
      return (
        <p className="text-base text-neutral-700">
          No providers are licensed in {stateName ?? "this state"} for follow-up
          visits.
        </p>
      );
    }

    return (
      <RadioGroup
        value={selectedProviderId}
        onChange={setSelectedProviderId}
        aria-label="Providers"
        className="flex flex-col gap-6"
      >
        <ProviderGroup
          title="In network providers"
          providers={inNetworkProviders}
        />
        <ProviderGroup
          title="Out of network providers"
          providers={outOfNetworkProviders}
        />
      </RadioGroup>
    );
  };

  return (
    <div className="flex min-h-full flex-col gap-6">
      <StepHeading title="Book a visit" visitType={VISIT_TYPES.FOLLOW_UP} />

      {renderContent()}

      <StaffBookingFooter
        onBack={onBack}
        continueProps={{
          type: "button",
          onClick: () => selectedProvider && onContinue(selectedProvider.id),
          disabled: !selectedProvider,
        }}
      />
    </div>
  );
};
